import { module as ModuleJS } from "modujs";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { debounce } from "lodash";

gsap.registerPlugin(ScrollTrigger);

export default class extends ModuleJS {
    constructor(m) {
        super(m);

        this.active = false;
        this.nav = this.$("nav")[0];
        this.buttons = this.$("button");
        this.menu = this.nav.querySelector(".c-navigation_menu");
        this.links = [];
        this.linksWithChildren = [];

        if (this.menu) {
            // Get all the link elements within the menu.
            this.links = this.menu.querySelectorAll("a");

            // Get all the link elements with children within the menu.
            this.linksWithChildren = this.menu.querySelectorAll(
                ".menu-item-has-children > a, .page_item_has_children > a"
            );
        }

        this.header = document.getElementById("header");
        this.fakeNavItems = this.header.querySelectorAll('[href="#"]');

        this.NAV_ACTIVE_CLASS = "is-nav-active";

        this.HEADER_SCROLLED_CLASS = "is-header-scrolled";
        this.SCROLLTRIGGER_POSITION = "100px";
        this.DURATION_IN = 0.5;
        this.DURATION_OUT = 0.3;
        this.EASE = "power1.inOut";
    }

    init() {
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.setCSSVariables = this.setCSSVariables.bind(this);
        this.setHeaderHeightCSSVariable =
            this.setHeaderHeightCSSVariable.bind(this);
        this.setWindowHeightCSSVariable =
            this.setWindowHeightCSSVariable.bind(this);
        this.swapMenu = this.swapMenu.bind(this);
        this.enableScrolledMode = this.enableScrolledMode.bind(this);
        this.disableScrolledMode = this.disableScrolledMode.bind(this);

        this.scrollTrigger = ScrollTrigger.create({
            start: this.SCROLLTRIGGER_POSITION,
            end: this.SCROLLTRIGGER_POSITION,
            onEnter: this.enableScrolledMode,
            onEnterBack: this.disableScrolledMode,
        });

        this.bindEvents();
        this.setCSSVariables();
    }

    swapMenu(fn) {
        const tl = gsap.timeline({
            onStart: () => {
                document.body.classList.add("is-header-swapping");
            },
            onComplete: () => {
                document.body.classList.remove("is-header-swapping");
            },
        });

        tl.to(header, {
            duration: this.DURATION_OUT,
            autoAlpha: 0,
            y: "-100%",
            snap: {
                x: 1,
                y: 1,
            },
            ease: "power3.easeIn",
        })
            .add(fn)
            .to(header, {
                duration: this.DURATION_IN,
                autoAlpha: 1,
                y: "0%",
                snap: {
                    x: 1,
                    y: 1,
                },
                clearProps: "all",
                ease: "power3.easeOut",
            });
    }

    getHeaderScrolledClass() {
        return this.HEADER_SCROLLED_CLASS;
    }

    enableScrolledMode() {
        this.swapMenu(() => {
            document.body.classList.add(this.HEADER_SCROLLED_CLASS);
        });
    }

    disableScrolledMode() {
        this.swapMenu(() => {
            document.body.classList.remove(this.HEADER_SCROLLED_CLASS);
        });
    }

    /** Menu height as padding to body */
    setHeaderHeightCSSVariable() {
        const isScrolled = document.body.classList.contains(
            this.HEADER_SCROLLED_CLASS
        );

        if (isScrolled)
            document.body.classList.remove(this.HEADER_SCROLLED_CLASS);

        const headerHeight = this.header.getBoundingClientRect().height;
        if (headerHeight) {
            document.documentElement.style.setProperty(
                "--header-height",
                headerHeight + "px"
            );
        }

        if (isScrolled) document.body.classList.add(this.HEADER_SCROLLED_CLASS);
    }

    setWindowHeightCSSVariable() {
        document.documentElement.style.setProperty(
            "--vh",
            `${window.innerHeight}px`
        );
    }

    setCSSVariables() {
        this.setHeaderHeightCSSVariable();
        this.setWindowHeightCSSVariable();
    }

    onButtonClick(e) {
        this.toggle();
    }

    toggle() {
        if (this.active) {
            this.hide();
        } else {
            this.show();
        }
    }

    show() {
        this.active = true;
        document.documentElement.classList.add(this.NAV_ACTIVE_CLASS);

        for (const button of this.buttons) {
            button.setAttribute("aria-expanded", "true");
        }
    }

    hide() {
        this.active = false;
        document.documentElement.classList.remove(this.NAV_ACTIVE_CLASS);

        for (const button of this.buttons) {
            button.setAttribute("aria-expanded", "false");
        }
    }

    onDocumentClick(event) {
        const isClickInside = this.nav.contains(event.target);

        if (!isClickInside) {
            this.hide();
        }
    }

    bindEvents(el) {
        // Toggle the .toggled class and the aria-expanded value each time the button is clicked.
        for (const button of this.buttons) {
            button.addEventListener("click", this.onButtonClick, true);
        }

        // Remove the .toggled class and set aria-expanded to false when the user clicks outside the navigation.
        document.addEventListener("click", this.onDocumentClick);

        // Toggle focus each time a menu link with children receive a touch event.
        for (const link of this.linksWithChildren) {
            link.addEventListener("click", this.toggleFocus, false);
        }

        document.addEventListener("DOMContentLoaded", this.setCSSVariables);
        window.addEventListener("resize", debounce(this.setCSSVariables, 50));

        // Désactivation clics a[href="#"]
        this.fakeNavItems.forEach(function (navItem) {
            navItem.addEventListener("click", function (e) {
                e.preventDefault();
            });
        });
    }

    update(newContainer) {
        this.bindEvents(newContainer);
    }

    destroy(oldContainer) {
        // this.el.removeEventListener("click", this.onClick);
    }

    /**
     * Sets or removes .focus class on an element.
     */
    toggleFocus(event) {
        if (event.type === "touchstart" || event.type === "click") {
            const menuItem = this.parentNode;
            event.preventDefault();
            for (const link of menuItem.parentNode.children) {
                if (menuItem !== link) {
                    link.classList.remove("-focus");
                }
            }
            menuItem.classList.toggle("-focus");

            if (
                menuItem.classList.contains("-focus") &&
                menuItem.scrollIntoView
            ) {
                requestAnimationFrame(() => {
                    menuItem.scrollIntoView();
                });
            }
        }
    }
}
